import StaffService from './StaffService';
import MessageService from './MessageService';

const STORAGE_KEY = 'safeguardpro_broadcasts_v1';
const ACK_KEY = 'safeguardpro_broadcast_acks_v1';

const readList = (key) => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeList = (key, list) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(list));
};

const normalizeBroadcast = (broadcast) => ({
  id: broadcast.id || `bc-${Math.random().toString(36).slice(2, 9)}`,
  title: broadcast.title || '',
  body: broadcast.body || '',
  severity: broadcast.severity || 'info',
  audience:
    broadcast.audience === 'selected'
      ? 'selected'
      : broadcast.audience === 'all'
        ? 'all'
        : broadcast.audience === 'all_staff'
          ? 'all'
          : broadcast.audience || 'all',
  audienceIds: Array.isArray(broadcast.audienceIds) ? broadcast.audienceIds : [],
  effective_from: broadcast.effective_from || new Date().toISOString(),
  expires_at: broadcast.expires_at || null,
  ack_required: broadcast.ack_required ?? true,
  ack_phrase: broadcast.ack_phrase || 'AGREE',
  status: broadcast.status || 'draft',
  version: broadcast.version || 1,
  created_by: broadcast.created_by || 'system',
  created_at: broadcast.created_at || new Date().toISOString(),
  updated_at: broadcast.updated_at || new Date().toISOString(),
  messageVersionSent: broadcast.messageVersionSent || 0
});

const normalizeAck = (ack) => ({
  id: ack.id || `ack-${Math.random().toString(36).slice(2, 9)}`,
  announcement_id: ack.announcement_id,
  user_id: ack.user_id,
  announcement_version: ack.announcement_version,
  acknowledged_at: ack.acknowledged_at || new Date().toISOString()
});

const isActive = (broadcast, now = new Date()) => {
  if (broadcast.status !== 'published') return false;
  const effective = broadcast.effective_from ? new Date(broadcast.effective_from) : null;
  const expires = broadcast.expires_at ? new Date(broadcast.expires_at) : null;
  if (effective && effective > now) return false;
  if (expires && expires < now) return false;
  return true;
};

export default class BroadcastService {
  constructor() {
    this.staffService = new StaffService();
    this.messageService = new MessageService();
  }

  list() {
    return readList(STORAGE_KEY).map((item) => normalizeBroadcast(item));
  }

  listAcknowledgments() {
    return readList(ACK_KEY).map((item) => normalizeAck(item));
  }

  saveAll(list) {
    writeList(STORAGE_KEY, list);
  }

  saveAcknowledgments(list) {
    writeList(ACK_KEY, list);
  }

  create(broadcast, user) {
    const next = normalizeBroadcast({
      ...broadcast,
      created_by: user?.name || user?.id || 'system',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
    const list = [next, ...this.list()];
    this.saveAll(list);
    return next;
  }

  update(broadcast, user) {
    let updatedRecord = null;
    const list = this.list().map((item) => {
      if (item.id !== broadcast.id) return item;
      const version = item.status === 'published' && broadcast.status === 'published' ? item.version + 1 : item.version;
      const next = normalizeBroadcast({
        ...item,
        ...broadcast,
        version,
        updated_at: new Date().toISOString(),
        created_by: item.created_by || user?.name || 'system'
      });
      updatedRecord = next;
      return next;
    });
    this.saveAll(list);
    if (updatedRecord && updatedRecord.status === 'published') {
      this.sendBroadcastMessage(updatedRecord);
    }
    return updatedRecord;
  }

  publish(id, user) {
    const list = this.list().map((item) => {
      if (item.id !== id) return item;
      const version = item.status === 'published' ? item.version + 1 : item.version;
      return normalizeBroadcast({
        ...item,
        status: 'published',
        version,
        updated_at: new Date().toISOString(),
        created_by: item.created_by || user?.name || 'system'
      });
    });
    this.saveAll(list);
    const broadcast = list.find((item) => item.id === id);
    if (broadcast) {
      this.sendBroadcastMessage(broadcast);
    }
    return broadcast || null;
  }

  expire(id) {
    const list = this.list().map((item) => {
      if (item.id !== id) return item;
      return normalizeBroadcast({
        ...item,
        status: 'expired',
        updated_at: new Date().toISOString()
      });
    });
    this.saveAll(list);
    return list.find((item) => item.id === id) || null;
  }

  getActive() {
    const now = new Date();
    return this.list().filter((broadcast) => isActive(broadcast, now));
  }

  getActiveForUser(userId) {
    const active = this.getActive();
    const acks = this.listAcknowledgments();
    return active.find((broadcast) => {
      if (broadcast.audience === 'selected' && !broadcast.audienceIds.includes(userId)) {
        return false;
      }
      if (!broadcast.ack_required) return true;
      const acknowledged = acks.some(
        (ack) =>
          ack.announcement_id === broadcast.id &&
          ack.user_id === userId &&
          ack.announcement_version === broadcast.version
      );
      return !acknowledged;
    });
  }

  acknowledge(broadcast, userId) {
    const acks = this.listAcknowledgments();
    const existing = acks.find(
      (ack) =>
        ack.announcement_id === broadcast.id &&
        ack.user_id === userId &&
        ack.announcement_version === broadcast.version
    );
    if (existing) return existing;
    const next = normalizeAck({
      announcement_id: broadcast.id,
      user_id: userId,
      announcement_version: broadcast.version
    });
    this.saveAcknowledgments([next, ...acks]);
    return next;
  }

  getAcknowledgmentStats(broadcast) {
    const staff = this.staffService.list().filter((member) => member.loginEnabled);
    const audience =
      broadcast.audience === 'selected'
        ? staff.filter((member) => broadcast.audienceIds.includes(member.id))
        : staff;
    const acks = this.listAcknowledgments().filter(
      (ack) => ack.announcement_id === broadcast.id && ack.announcement_version === broadcast.version
    );
    const audienceIds = new Set(audience.map((member) => member.id));
    const acknowledged = acks.filter((ack) => audienceIds.has(ack.user_id)).length;
    return { total: audience.length, acknowledged };
  }

  sendBroadcastMessage(broadcast) {
    if (broadcast.messageVersionSent === broadcast.version) return;
    const staffRecipients = this.staffService
      .list()
      .filter((member) => member.loginEnabled)
      .filter((member) =>
        broadcast.audience === 'selected' ? broadcast.audienceIds.includes(member.id) : true
      )
      .map((member) => ({ id: member.id, name: member.name, type: 'staff' }));
    if (staffRecipients.length === 0) return;

    this.messageService.send({
      subject: `Broadcast: ${broadcast.title}`,
      body: broadcast.body,
      fromUser: { id: 'system', name: 'SafeguardPro' },
      recipients: staffRecipients,
      references: [
        {
          type: 'broadcast',
          id: broadcast.id,
          label: `Announcement: ${broadcast.title}`,
          route: '/broadcasts'
        }
      ]
    });

    const list = this.list().map((item) => {
      if (item.id !== broadcast.id) return item;
      return { ...item, messageVersionSent: broadcast.version };
    });
    this.saveAll(list);
  }
}
